import { useState } from 'react';
import { isTauri } from '@wordpuppi/shared';
import { toast } from 'sonner';
import { Check, File as FileIcon, FileCheck, FolderOpen, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  serializePost,
  parsePost,
  type PostFrontMatter,
} from '@/utils/frontmatter';

/**
 * AB#426: server/disk state chips + local Save/Open controls for the post editor.
 *
 * - Server chip (web AND desktop, needs #430 dirty tracking): Saved / Unsaved changes.
 * - Disk chip (desktop-only, isTauri-gated, appears after the first export this
 *   session): On disk / Disk copy stale — tooltip = absolute path + export time.
 * - Save/Open buttons are desktop-only; the Tauri plugins are dynamically imported so
 *   the plain-browser bundle never loads them.
 *
 * State matrix (all five rows):
 *   clean + never-exported        → Saved
 *   dirty + never-exported        → Unsaved changes
 *   exported + unedited           → Saved + On disk
 *   exported-then-edited          → Unsaved changes + Disk copy stale
 *   server-saved while disk stale → Saved + Disk copy stale (disk state is independent)
 */

interface Props {
  /** Current front-matter (what would be written now). */
  fm: PostFrontMatter;
  /** Current body markdown (already Markdown-ized by the caller). */
  body: string;
  /** Server dirty flag from the #430 unsaved-changes tracking. */
  dirty: boolean;
  /** Called with parsed front-matter + body when a file is opened from disk. */
  onOpen?: (parsed: { frontMatter: PostFrontMatter; body: string }) => void;
}

const WORKSPACE_DIR_KEY = 'wpp-workspace-dir';

export function EditorDiskBar({ fm, body, dirty, onOpen }: Props) {
  const tauri = isTauri();
  // Last export this session: the exact md we wrote, its abs path, and when.
  const [exported, setExported] = useState<{ content: string; path: string; at: string } | null>(
    null,
  );

  const currentMd = serializePost(fm, body);
  const diskStale = exported !== null && exported.content !== currentMd;

  const filename = `${fm.slug || fm.id || 'untitled'}.md`;

  const doExport = async (existingPath?: string) => {
    try {
      const { save } = await import('@tauri-apps/plugin-dialog');
      const { writeTextFile } = await import('@tauri-apps/plugin-fs');

      let path = existingPath;
      if (!path) {
        const workspaceDir = localStorage.getItem(WORKSPACE_DIR_KEY) ?? undefined;
        const picked = await save({
          defaultPath: workspaceDir ? `${workspaceDir}/${filename}` : filename,
          filters: [{ name: 'Markdown', extensions: ['md'] }],
        });
        if (!picked) return; // cancelled — no-op, no toast, no partial write
        path = picked;
        // Remember the directory so the next export defaults alongside it.
        const dir = picked.slice(0, Math.max(picked.lastIndexOf('/'), picked.lastIndexOf('\\')));
        if (dir) localStorage.setItem(WORKSPACE_DIR_KEY, dir);
      }

      await writeTextFile(path, currentMd);
      const at = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      setExported({ content: currentMd, path, at });
      toast.success(`Saved to ${path}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save to disk');
    }
  };

  const doOpen = async () => {
    try {
      const { open } = await import('@tauri-apps/plugin-dialog');
      const { readTextFile } = await import('@tauri-apps/plugin-fs');
      const picked = await open({
        multiple: false,
        filters: [{ name: 'Markdown', extensions: ['md'] }],
      });
      if (!picked || typeof picked !== 'string') return; // cancelled — no-op
      const text = await readTextFile(picked);
      onOpen?.(parsePost(text));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to open file');
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Server chip — web + desktop. Purely additive. */}
      <span
        className={
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ' +
          (dirty ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' : 'bg-muted text-muted-foreground')
        }
      >
        {dirty ? (
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
        ) : (
          <Check className="h-3.5 w-3.5" aria-hidden />
        )}
        {dirty ? 'Unsaved changes' : 'Saved'}
      </span>

      {/* Disk chip — desktop-only, after first export this session. */}
      {tauri && exported && (
        <button
          type="button"
          onClick={() => doExport(diskStale ? exported.path : undefined)}
          title={`${exported.path} — saved ${exported.at}`}
          className={
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-[color,background-color,opacity,scale] duration-(--duration-popover) ease-out starting:scale-95 starting:opacity-0 motion-reduce:starting:scale-100 ' +
            (diskStale
              ? 'bg-amber-500/15 text-amber-600 hover:bg-amber-500/25 dark:text-amber-400'
              : 'bg-muted text-muted-foreground hover:bg-muted/70')
          }
        >
          {diskStale ? <FileIcon className="h-3.5 w-3.5" aria-hidden /> : <FileCheck className="h-3.5 w-3.5" aria-hidden />}
          {diskStale ? 'Disk copy stale' : 'On disk'}
        </button>
      )}

      {/* Save/Open — desktop-only. */}
      {tauri && (
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => doExport(exported?.path)}
          >
            <Save className="mr-1.5 h-4 w-4" />
            Save to disk
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={doOpen}>
            <FolderOpen className="mr-1.5 h-4 w-4" />
            Open from disk
          </Button>
        </>
      )}
    </div>
  );
}
